import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';

import { useExpandableText } from '@/hooks/useExpandableText';

/**
 * A palette slot, a stated colour, or one of each per breakpoint — which is
 * what the opening band passes, because it writes in the palette's ink below
 * `md` and in stated inverse values above it (pageIntroInk.ts).
 */
type Ink = string | Record<string, string>;

/**
 * The lede's size, and why it steps down on a phone.
 *
 * `subtitle1` is 20px, which is right for a sentence that sits under a 31px
 * display heading. Under a 20px one it is the same size as the heading, and
 * two lines of it is 60px of a 390px screen. 17px keeps it clearly a lede and
 * clearly subordinate.
 */
const SIZE = { xs: '1.0625rem', md: '1.25rem' };

/**
 * Two lines on a phone, the whole thing from `md` up.
 *
 * `-webkit-line-clamp` and not a character count, because the clamp is a
 * property of the rendered line and a count is a guess about it: the same 100
 * characters set on two lines at 430px and three at 320px. It needs
 * `display: -webkit-box` to take effect at all, which is why `display` is
 * stated here too and switched back to `block` at `md` — the box model is
 * wrong for a paragraph that is allowed to run.
 */
const CLAMPED = {
  display: { xs: '-webkit-box', md: 'block' },
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: { xs: '2', md: 'unset' },
  overflow: { xs: 'hidden', md: 'visible' },
};

type Props = {
  text: string;
  /** The reading measure. The band and a section band hold different ones. */
  maxWidth: string;
  /** The text's own ink. */
  color: Ink;
  /** On a dark surface, so the toggle is legible on it too. */
  inverse?: boolean;
  /** Smaller, tighter copy for compact catalogue headers. */
  compact?: boolean;
};

/**
 * A lede that is cut to two lines on a phone and can be opened.
 *
 * The clamp on its own was a plain truncation: the ellipsis said there was
 * more and offered no way to reach it, which is fine for a label and not fine
 * for the sentence that explains what a page or a band is for. This is the
 * same clamp with the rest of the sentence one tap away.
 *
 * The toggle is only drawn when the text is genuinely cut — see
 * `useExpandableText` for why that has to be measured — and only below `md`,
 * where the clamp exists. Above it the paragraph is whole and a "Read more"
 * would open nothing.
 *
 * `type="button"`, because `Link component="button"` renders a real `<button>`
 * and a button inside a form defaults to `submit`. `SectionHeading` is used
 * inside the quotation form, so without this, opening a lede would post it.
 */
export function ExpandableLede({ text, maxWidth, color, inverse, compact = false }: Props) {
  const { ref, expanded, truncated, toggle, contentId } = useExpandableText();

  return (
    <>
      <Typography
        id={contentId}
        ref={ref}
        variant="subtitle1"
        sx={[
          {
            maxWidth,
            fontSize: compact ? { xs: '0.9375rem', md: '1.0625rem' } : SIZE,
            ...(compact && { lineHeight: 1.45 }),
            color,
          },
          !expanded && CLAMPED,
        ]}
      >
        {text}
      </Typography>
      {truncated && (
        <Link
          component="button"
          type="button"
          onClick={toggle}
          aria-expanded={expanded}
          aria-controls={contentId}
          sx={{
            // Below `md` only: there is no clamp above it to undo.
            display: { xs: 'inline-flex', md: 'none' },
            alignSelf: 'flex-start',
            // Quieter than the heading above it and lighter than a button:
            // this opens a sentence, it does not start anything.
            fontSize: '0.875rem',
            fontWeight: 600,
            color: inverse ? 'primary.light' : 'primary.main',
            textDecorationColor: 'currentColor',
          }}
        >
          {expanded ? 'Read less' : 'Read more'}
        </Link>
      )}
    </>
  );
}
